/**
 * O que as automações fizeram, em palavras: o resultado, o dia, o passo.
 *
 * A execução não traz contato nem texto livre do serviço: só ids, o tipo de
 * cada passo e um motivo em chave curta (`acao_recusada`). A tela diz o passo
 * pelo nome do catálogo e mostra a chave só como código para o suporte.
 */

import type { BlocosDoEditor } from '@cliqcrm/contracts';
import type { TomSelo } from '@/shared/ui';

export type Resultado = 'concluida' | 'ignorada' | 'falhou';

export const RESULTADO: Record<Resultado, { rotulo: string; tom: TomSelo }> = {
  concluida: { rotulo: 'Concluída', tom: 'ok' },
  ignorada: { rotulo: 'Não rodou', tom: 'neutro' },
  falhou: { rotulo: 'Falhou', tom: 'erro' },
};

export type FiltroDoResultado = 'todas' | Resultado;

export const FILTROS_DO_RESULTADO: readonly { valor: FiltroDoResultado; rotulo: string }[] = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'concluida', rotulo: 'Concluídas' },
  { valor: 'falhou', rotulo: 'Falharam' },
  { valor: 'ignorada', rotulo: 'Não rodaram' },
];

type Passo = { no: string | null; tipo: string; resultado: Resultado; saida: string | null };

/** O nome de um passo: a ação ou o bloco pelo catálogo. */
export function tituloDoPasso(tipo: string, blocos: BlocosDoEditor | undefined): string {
  return (
    blocos?.acoes.find((a) => a.acao === tipo)?.titulo ??
    blocos?.nos.find((n) => n.tipo === tipo)?.titulo ??
    'Um passo'
  );
}

/** O que aconteceu, numa linha: onde parou, ou o último passo. */
export function oQueAconteceu(
  e: { resultado: Resultado; passos: readonly Passo[]; motivo?: string | null },
  blocos: BlocosDoEditor | undefined,
): string {
  if (e.resultado === 'ignorada') return 'O evento não cumpriu a condição';
  // O teto de voltas do serviço (decisão de 28/10): o fluxo voltou demais ao mesmo bloco.
  if (e.resultado === 'falhou' && e.motivo === 'loop_limit_exceeded') {
    return 'Parou: o cliente voltou ao mesmo bloco mais de 10 vezes';
  }
  if (e.resultado === 'falhou') {
    const onde = e.passos.find((p) => p.resultado === 'falhou') ?? e.passos.at(-1);
    return onde ? `Parou em: ${tituloDoPasso(onde.tipo, blocos)}` : 'Parou antes do primeiro bloco';
  }
  const ultimo = e.passos.at(-1);
  return ultimo ? tituloDoPasso(ultimo.tipo, blocos) : 'Não passou por nenhum bloco';
}

const DIA = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const HORA = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });

export function hora(iso: string): string {
  return HORA.format(new Date(iso));
}

function inicioDoDia(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** "Hoje", "Ontem" ou a data: o grupo de cada execução, na ordem em que vêm. */
export function agruparPorDia<T extends { em: string }>(
  itens: readonly T[],
  agora: Date,
): { rotulo: string; itens: T[] }[] {
  const hoje = inicioDoDia(agora);
  // O dia de ontem pelo calendário: no dia em que o relógio muda, não são 24 h.
  const ontem = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - 1).getTime();
  const grupos: { rotulo: string; itens: T[] }[] = [];
  for (const item of itens) {
    const dia = inicioDoDia(new Date(item.em));
    const rotulo = dia === hoje ? 'Hoje' : dia === ontem ? 'Ontem' : DIA.format(new Date(item.em));
    const ultimo = grupos.at(-1);
    if (ultimo?.rotulo === rotulo) ultimo.itens.push(item);
    else grupos.push({ rotulo, itens: [item] });
  }
  return grupos;
}

/** "há 5 min", "há 2 h", "ontem": desde quando, para quem lê de relance. */
export function relativo(iso: string, agora: Date): string {
  const minutos = Math.floor((agora.getTime() - new Date(iso).getTime()) / 60_000);
  if (minutos < 1) return 'agora há pouco';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  // Os dias pelo calendário, como o agrupamento: "ontem" é o dia de ontem.
  const dias = Math.round((inicioDoDia(agora) - inicioDoDia(new Date(iso))) / 86_400_000);
  return dias <= 1 ? 'ontem' : `há ${dias} dias`;
}

/** "0,3 s", "12 s", "2 min": quanto a execução levou. */
export function duracao(ms: number): string {
  if (ms < 10_000) return `${(ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s`;
  if (ms < 120_000) return `${Math.round(ms / 1000)} s`;
  return `${Math.round(ms / 60_000)} min`;
}
